import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { AutoRefreshToolbar, CollapsiblePanel } from '@/components/common';
import { ErrorBanner, StatusBadge } from '@/components/ui';
import { api } from '@/api/client';
import { useMe } from '@/context/MeContext';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import type { HealthData } from '@/types';

type RuntimePublicInfo = {
  gitCommitShort?: string;
  gitBranch?: string;
  imageTag?: string;
  buildDate?: string | null;
  environment?: string;
  services?: Array<{ name: string; status: string }>;
};

function statusVariant(status: string | undefined): 'success' | 'warning' | 'danger' | 'neutral' {
  const normalized = (status || '').toLowerCase();
  if (['healthy', 'ok', 'up', 'running', 'connected'].includes(normalized)) return 'success';
  if (['degraded', 'warning', 'busy'].includes(normalized)) return 'warning';
  if (['unhealthy', 'failed', 'down', 'error'].includes(normalized)) return 'danger';
  return 'neutral';
}

export default function ReleaseHealthPage() {
  const { me } = useMe();
  const canView = me?.permissions.canViewDiagnostics ?? false;
  const [health, setHealth] = useState<HealthData | null>(null);
  const [runtime, setRuntime] = useState<RuntimePublicInfo | null>(null);
  const [diagnostics, setDiagnostics] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!canView) return;
    setError('');
    try {
      const [healthData, diagData, publicInfo] = await Promise.all([
        api.dashboard.health(),
        api.dashboard.diagnostics().catch(() => null),
        fetch('/api/runtime/public-info')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      ]);
      setHealth(healthData);
      setDiagnostics(diagData as Record<string, unknown> | null);
      setRuntime(publicInfo as RuntimePublicInfo | null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load release health');
    }
  }, [canView]);

  const { autoRefresh } = useAutoRefreshPolling({
    hasData: () => Boolean(health),
    load,
    autoRefresh: {
      intervalMs: 30_000,
      storageKey: 'release-health-auto-refresh',
      enabled: canView,
    },
  });

  useEffect(() => {
    void load();
  }, [load]);

  if (!canView) {
    return (
      <>
        <PageHeader title="Release health" subtitle="Platform diagnostics required." />
        <p className="muted">You do not have permission to view release health.</p>
      </>
    );
  }

  const overall = health?.status;
  const checks = (diagnostics?.checks as Array<{ name: string; status: string; message?: string }>) || [];
  const systemChecks = health?.systemHealth?.checks ?? [];
  const operationalChecks = health?.operationalHealth?.checks ?? [];
  const telegramCheck = systemChecks.find((check) => check.code === 'telegram');
  const workerChecks = operationalChecks.filter((check) =>
    ['scrape_worker', 'send_worker', 'scrape_worker_missing', 'scrape_worker_stale', 'send_worker_missing', 'send_worker_stale'].includes(
      check.code,
    ),
  );

  return (
    <>
      <PageHeader
        title="Release health"
        subtitle="Pre-release gate — infrastructure, workers, and regression status."
      >
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 30s" />
      </PageHeader>

      {error ? <ErrorBanner message={error} /> : null}

      <section className="metric-grid">
        <div className="metric-card">
          <span className="metric-label">Overall</span>
          <StatusBadge variant={statusVariant(overall)}>{overall || 'unknown'}</StatusBadge>
        </div>
        <div className="metric-card">
          <span className="metric-label">Git commit</span>
          <strong>{runtime?.gitCommitShort || '—'}</strong>
        </div>
        <div className="metric-card">
          <span className="metric-label">Branch</span>
          <strong>{runtime?.gitBranch || '—'}</strong>
        </div>
        <div className="metric-card">
          <span className="metric-label">Image</span>
          <strong>{runtime?.imageTag || 'local'}</strong>
        </div>
      </section>

      <CollapsiblePanel id="release-health-runtime" storageScope="release-health" title="Runtime services" defaultExpanded>
        <ul className="metric-list">
          {(runtime?.services || []).map((svc) => (
            <li key={svc.name}>
              {svc.name}: <StatusBadge variant={statusVariant(svc.status)}>{svc.status}</StatusBadge>
            </li>
          ))}
          {!runtime?.services?.length ? <li className="muted">No public runtime service data.</li> : null}
        </ul>
      </CollapsiblePanel>

      <CollapsiblePanel id="release-health-workers" storageScope="release-health" title="Workers & Telegram" defaultExpanded>
        <ul className="metric-list">
          <li>
            Telegram:{' '}
            <StatusBadge variant={statusVariant(telegramCheck?.status || telegramCheck?.state)}>
              {telegramCheck?.status || telegramCheck?.state || 'unknown'}
            </StatusBadge>
            {telegramCheck?.message ? ` — ${telegramCheck.message}` : ''}
          </li>
          {workerChecks.map((worker) => (
            <li key={worker.code}>
              {worker.code.replace(/_/g, ' ')}:{' '}
              <StatusBadge variant={statusVariant(worker.status)}>{worker.status}</StatusBadge>
              {worker.message ? ` — ${worker.message}` : ''}
            </li>
          ))}
          {!workerChecks.length ? <li className="muted">No worker health checks loaded.</li> : null}
        </ul>
      </CollapsiblePanel>

      <CollapsiblePanel id="release-health-diagnostics" storageScope="release-health" title="Diagnostics checks">
        {checks.length === 0 ? (
          <p className="muted">No diagnostics checks loaded.</p>
        ) : (
          <ul className="metric-list">
            {checks.map((check) => (
              <li key={check.name}>
                {check.name}:{' '}
                <StatusBadge variant={statusVariant(check.status)}>{check.status}</StatusBadge>
                {check.message ? ` — ${check.message}` : ''}
              </li>
            ))}
          </ul>
        )}
      </CollapsiblePanel>

      <CollapsiblePanel id="release-health-regression" storageScope="release-health" title="Regression gate">
        <p>Run the local release gate before deploying:</p>
        <pre className="code-block">{`make dev-seed-demo   # populate demo orgs\nmake verify-local    # full regression gate`}</pre>
        <p>
          Reports: <code>docs/RELEASE_HEALTH_REPORT.md</code>,{' '}
          <code>docs/E2E_REGRESSION_REPORT.md</code>
        </p>
        <p>
          <Link to="/pipeline">Pipeline diagnostics</Link> · <Link to="/config">Configuration</Link> ·{' '}
          <Link to="/">System dashboard</Link>
        </p>
      </CollapsiblePanel>
    </>
  );
}
